import { get, set } from "lodash";
import { toast } from "react-toastify";
import type { SpecsModel } from "./specsModel/SpecsModel";
import {
  collectProductAssetPathsFromValue,
  isSpecsRecord,
  type SpecsModelAssetPath,
} from "./specsModel/productGrayscaleAssets";
import {
  getSpecsImageDimensions,
  slugFileSegment,
  uploadSpecsGrayscaleImageUrl,
  uploadSpecsImageUrlResizedToDimensions,
  uploadSpecsRGBAImageUrl,
  type SpecsImageDimensions,
} from "./specsImageUpload";

type UploadNewGrayscaleAssetsArgs = {
  model: SpecsModel;
  specName: string;
};
type UploadRGBAAssetsArgs = {
  snapshot: Record<string, unknown>;
  model: SpecsModel;
  specName: string;
};
type UnifyProductImageDimensionsArgs = {
  snapshot: Record<string, unknown>;
  specName: string;
};

function pathToFieldPath(path: SpecsModelAssetPath) {
  return path.join(".");
}

function buildGrayscaleAssetFileNameBase(
  specName: string,
  path: SpecsModelAssetPath
) {
  return [specName, ...path.map(String)]
    .map(slugFileSegment)
    .filter(Boolean)
    .join("-");
}

function buildRGBAAssetFileNameBase(
  specName: string,
  path: SpecsModelAssetPath
) {
  return [specName, ...path.map(String)]
    .map(slugFileSegment)
    .filter(Boolean)
    .join("-");
}

function dimensionsMatch(
  actual: SpecsImageDimensions,
  expected: SpecsImageDimensions
) {
  return actual.width === expected.width && actual.height === expected.height;
}

function collectLayerSideAssetPaths(
  version: Record<string, unknown>,
  versionIndex: number,
  sideName: string
): SpecsModelAssetPath[] {
  return collectProductAssetPathsFromValue(
    version.parts,
    ["imageUrl", "highlightUrl"],
    ["parts"]
  )
    .filter((path) => get(version, path.slice(0, -1))?.name === sideName)
    .map((path) => ["versions", versionIndex, ...path]);
}

async function normalizeImageAtPathToDimensions({
  snapshot,
  specName,
  path,
  expectedDimensions,
}: {
  snapshot: Record<string, unknown>;
  specName: string;
  path: SpecsModelAssetPath;
  expectedDimensions: SpecsImageDimensions;
}) {
  const imageUrl = get(snapshot, path);
  if (typeof imageUrl !== "string" || !imageUrl.trim()) {
    return false;
  }

  const actualDimensions = await getSpecsImageDimensions(imageUrl);
  if (dimensionsMatch(actualDimensions, expectedDimensions)) {
    return false;
  }

  const assetUrl = await uploadSpecsImageUrlResizedToDimensions(
    imageUrl,
    buildRGBAAssetFileNameBase(specName, path),
    expectedDimensions,
    {
      specType: "product",
      specName,
      fieldPath: pathToFieldPath(path),
    }
  );
  set(snapshot, path, assetUrl);
  return true;
}

export async function unifyProductImageDimensions({
  snapshot,
  specName,
}: UnifyProductImageDimensionsArgs) {
  const versions = Array.isArray(snapshot.versions) ? snapshot.versions : [];
  let resizedCount = 0;

  for (let versionIndex = 0; versionIndex < versions.length; versionIndex++) {
    const version = versions[versionIndex];
    if (!isSpecsRecord(version)) {
      continue;
    }
    const generator = isSpecsRecord(version.generator)
      ? version.generator
      : null;
    const sides = Array.isArray(generator?.sides) ? generator.sides : [];

    for (let sideIndex = 0; sideIndex < sides.length; sideIndex++) {
      const side = sides[sideIndex];
      if (!isSpecsRecord(side) || typeof side.name !== "string") {
        continue;
      }
      const sideImagePath: SpecsModelAssetPath = [
        "versions",
        versionIndex,
        "generator",
        "sides",
        sideIndex,
        "imageUrl",
      ];
      const sideImageUrl = get(snapshot, sideImagePath);
      if (typeof sideImageUrl !== "string" || !sideImageUrl.trim()) {
        continue;
      }

      let expectedDimensions: SpecsImageDimensions;
      try {
        expectedDimensions = await getSpecsImageDimensions(sideImageUrl);
      } catch (error) {
        console.error(
          `Failed to read product side image dimensions for ${pathToFieldPath(sideImagePath)}`,
          error
        );
        continue;
      }

      const paths: SpecsModelAssetPath[] = [];
      if (typeof side.highlightUrl === "string" && side.highlightUrl) {
        paths.push([
          "versions",
          versionIndex,
          "generator",
          "sides",
          sideIndex,
          "highlightUrl",
        ]);
      }
      paths.push(
        ...collectLayerSideAssetPaths(version, versionIndex, side.name)
      );

      for (const path of paths) {
        try {
          if (
            await normalizeImageAtPathToDimensions({
              snapshot,
              specName,
              path,
              expectedDimensions,
            })
          ) {
            resizedCount += 1;
          }
        } catch (error) {
          console.error(
            `Failed to resize product image for ${pathToFieldPath(path)}`,
            error
          );
        }
      }
    }
  }

  return {
    snapshot,
    resizedCount,
  };
}

export async function uploadNewGrayscaleAssets({
  model,
  specName,
}: UploadNewGrayscaleAssetsArgs) {
  const snapshot = model.save() as Record<string, unknown>;
  const paths = model.product.getGrayscaleAssetPaths();
  let hasShownUploadToast = false;
  let uploadedCount = 0;

  for (const path of paths) {
    const imageUrl = get(snapshot, path);
    if (typeof imageUrl !== "string" || !imageUrl.trim()) {
      continue;
    }

    let result;
    try {
      result = await uploadSpecsGrayscaleImageUrl(
        imageUrl,
        buildGrayscaleAssetFileNameBase(specName, path),
        {
          specType: "product",
          specName,
          fieldPath: pathToFieldPath(path),
        }
      );
    } catch (error) {
      console.error(
        `Failed to create grayscale specs asset for ${pathToFieldPath(path)}`,
        error
      );
      continue;
    }

    if (!result.uploaded) {
      continue;
    }

    if (!hasShownUploadToast) {
      toast.info("Uploading grayscale mask image versions");
      hasShownUploadToast = true;
    }

    uploadedCount += 1;
    set(snapshot, path, result.assetUrl);
  }

  return {
    snapshot,
    uploadedCount,
  };
}

export async function uploadRGBAAssets({
  snapshot,
  model,
  specName,
}: UploadRGBAAssetsArgs) {
  const paths = model.product.getRGBAAssetPaths();
  let uploadedCount = 0;

  for (const path of paths) {
    const imageUrl = get(snapshot, path);
    if (typeof imageUrl !== "string" || !imageUrl.trim()) {
      continue;
    }

    let result;
    try {
      // Generator reads highlights with readRGBA(), so single-channel PNGs need
      // to be expanded before the revision is saved.
      result = await uploadSpecsRGBAImageUrl(
        imageUrl,
        buildRGBAAssetFileNameBase(specName, path),
        {
          specType: "product",
          specName,
          fieldPath: pathToFieldPath(path),
        }
      );
    } catch (error) {
      console.error(
        `Failed to create RGBA specs asset for ${pathToFieldPath(path)}`,
        error
      );
      continue;
    }

    if (!result.uploaded) {
      continue;
    }

    uploadedCount += 1;
    set(snapshot, path, result.assetUrl);
  }

  return {
    snapshot,
    uploadedCount,
  };
}
